import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleSpin, handleSimulate } from "../../service";
import { SimulationSettings } from "../../settings";

// Thunk for spinning the reels, handles both real and simulated spins
export const rgsPlay = createAsyncThunk(
  "spin/rgsPlay",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet, token } = state.game; // Extract bet and token from the game state

    try {
      let result;
      if (state.settings.simulationModeEnabled) {
        // Use handleSimulate if simulation mode is active
        result = await handleSimulate(
          SimulationSettings.SIMULATION_SLOTFACE,
          bet,
          token
        );
      } else {
        // Use handleSpin otherwise
        result = await handleSpin(bet, token);
      }
      return result; // Return the result of the spin
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error.message || "Failed to spin the reels");
    }
  }
);

// Initial state for the spin slice
const initialState = {
  isSpinning: false, // Indicates if a spin is currently in progress
  status: "idle", // Tracks the status of the API request
  error: null, // Stores any error messages
  manualStop: false, // Flag to track if the spin was manually stopped
  spinComplete: true, // Indicates if the spin has completed
  counterAnimationFinished: false, // Tracks if the counter animation is finished
  linesRotationFinished: false, // Tracks if the lines rotation is finished
  currentWinningLine: null, // Tracks the current winning line
};

// Create a slice for spin-related state and actions
const spinSlice = createSlice({
  name: "spin",
  initialState,
  reducers: {
    // Action to manually stop a spin
    manualSpinStop: (state) => {
      if (
        state.status === "succeeded" &&
        state.isSpinning &&
        !state.manualStop
      ) {
        state.manualStop = true; // Mark the stop as manual
      }
    },

    // Action to manually set spinning state
    manualSpinStart: (state) => {
      state.isSpinning = true; // Set isSpinning
      state.spinComplete = false; // Set spinComplete
    },

    // Action to set the spin completion status
    setSpinComplete: (state, action) => {
      if (state.status === "succeeded") {
        state.isSpinning = false; // Mark spinning as false
        state.spinComplete = action.payload; // Update spin completion status
      }
    },

    // Action to set the counter animation finished status
    setCounterAnimationFinished: (state, action) => {
      state.counterAnimationFinished = action.payload; // Update counter animation status
    },

    // Action to set the lines rotation finished status
    setLinesRotationFinished: (state, action) => {
      state.linesRotationFinished = action.payload; // Update lines rotation status
    },

    // Action to set the current winning line
    setCurrentWinningLine: (state, action) => {
      state.currentWinningLine = action.payload; // Update current winning line
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rgsPlay.pending, (state, action) => {
        state.error = null; // Clear any previous errors
        state.status = "pending"; // Set status to pending
        state.isSpinning = true; // Mark spinning as true
        state.spinComplete = false; // Mark spin as not complete
        state.manualStop = false; // Reset manual stop flag
      })
      .addCase(rgsPlay.fulfilled, (state) => {
        state.status = "succeeded"; // Set status to succeeded
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        state.status = "failed"; // Set status to failed
        state.isSpinning = false; // Mark spinning as false
        state.error = action.payload || "Spin failed"; // Set error message
      });
  },
});

// Export actions for use in components
export const {
  manualSpinStop,
  manualSpinStart,
  setSpinComplete,
  setCounterAnimationFinished,
  setLinesRotationFinished,
  setCurrentWinningLine,
} = spinSlice.actions;

// Export the reducer to be included in the store
export default spinSlice.reducer;
